import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom"; 
import PimpinanLayout from "../../components/layout/PimpinanLayout";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ChevronLeft, ChevronRight, HelpCircle } from "lucide-react"; 

export default function DashboardPimpinan() {
  const token = sessionStorage.getItem("token");

  const [summary, setSummary] = useState({
    hadir: 0,
    izin: 0,
    sakit: 0,
    alfa: 0,
  });

  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    fetchDashboard();

    const interval = setInterval(() => {
      fetchDashboard();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(
        "https://backend.smpn1kamal.sch.id/api/dashboard-global",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = res.data;
      setSummary(data);

      setChartData([
        {
          time: "Hari Ini",
          hadir: data.hadir,
          izin: data.izin,
          sakit: data.sakit,
          alfa: data.alfa,
        },
      ]);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <PimpinanLayout>
      {/* HEADER UTAMA SEJAJAR DENGAN TOMBOL PETUNJUK KAPSUL BIRU */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-blue-600">Beranda</h1>
          <p className="text-xs text-slate-500">SMPN 1 Kamal</p>
        </div>
        
        {/* TOMBOL PETUNJUK KAPSUL BIRU */}
        <Link
          to="/pimpinan/petunjuk"
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-full text-xs font-semibold shadow-sm transition-all"
        >
          <HelpCircle size={15} />
          Petunjuk
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard title="Hadir" subtitle="Hari Ini" value={summary.hadir} color="emerald" />
        <StatCard title="Izin" subtitle="Hari Ini" value={summary.izin} color="indigo" />
        <StatCard title="Sakit" subtitle="Hari Ini" value={summary.sakit} color="orange" />
        <StatCard title="Alfa" subtitle="Hari Ini" value={summary.alfa} color="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl shadow p-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">
            Laporan <span className="text-xs text-slate-400">/ Hari ini</span>
          </h2>

          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Line type="monotone" dataKey="hadir" stroke="#10b981" strokeWidth={2} />
                <Line type="monotone" dataKey="izin" stroke="#6366f1" strokeWidth={2} />
                <Line type="monotone" dataKey="sakit" stroke="#f97316" strokeWidth={2} />
                <Line type="monotone" dataKey="alfa" stroke="#ef4444" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <CalendarComponent />
      </div>
    </PimpinanLayout>
  );
}

function CalendarComponent() {
  const year = new Date().getFullYear();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());

  const monthNames = [
    "Januari","Februari","Maret","April",
    "Mei","Juni","Juli","Agustus",
    "September","Oktober","November","Desember"
  ];

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = new Date(year, month, 1).getDay();

  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setMonth((m) => Math.max(0, m - 1))}
          disabled={month === 0}
          className="p-1.5 rounded-full hover:bg-slate-100 disabled:opacity-30"
        >
          <ChevronLeft size={18} />
        </button>

        <h2 className="text-sm font-semibold text-slate-700">
          {monthNames[month]} {year}
        </h2>

        <button
          onClick={() => setMonth((m) => Math.min(11, m + 1))}
          disabled={month === 11}
          className="p-1.5 rounded-full hover:bg-slate-100 disabled:opacity-30"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-7 text-xs text-center text-slate-500 mb-2">
        {["Min","Sen","Sel","Rab","Kam","Jum","Sab"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 text-sm">
        {days.map((day, i) => {
          const isToday =
            day &&
            today.getDate() === day &&
            today.getMonth() === month;

          return (
            <div
              key={i}
              className={`h-9 flex items-center justify-center rounded
                ${day ? "hover:bg-slate-100" : ""}
                ${isToday ? "bg-blue-600 text-white font-semibold" : ""}
              `}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ title, subtitle, value, color }) {
  const colors = {
    emerald: "bg-emerald-400 text-emerald-500",
    indigo: "bg-indigo-500 text-indigo-500",
    orange: "bg-orange-500 text-orange-500",
    red: "bg-red-500 text-red-500",
  };

  return (
    <div className="bg-white rounded-xl shadow p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-full ${colors[color].split(" ")[0]}`} />
      <div>
        <p className={`text-sm font-semibold ${colors[color].split(" ")[1]}`}>
          {title}
          <span className="text-slate-400 font-normal"> | {subtitle}</span>
        </p>
        <h2 className="text-xl font-bold text-slate-800 mt-1">
          {value}
        </h2>
      </div>
    </div>
  );
}